import styled from 'styled-components';

import { ProductFlow } from 'data/__generated__';

import { formatDateAsAnniversary } from 'helpers/dateTimes';

import { BundleTypeTag } from 'pages/Bundles/components/TypeTag';

import { BundleRow } from '../constants/bundlesColumns';

type FlowSelectionButtonsProps = {
    bundles: BundleRow[];
    selectedIds: string[];
    onSelectionChange: (ids: string[]) => void;
};

type FlowStats = {
    bundles: number;
    boxes: number;
    deliveries: number;
    products: number;
    references: number;
};

const computeFlowStats = (flowBundles: BundleRow[]): FlowStats => {
    const deliveryIds = new Set<string>();
    // References must be deduplicated across the whole flow: bundle.numberOfReferences is already
    // distinct *within* a bundle, so summing it double-counts a reference shared by several bundles.
    const referenceIds = new Set<string>();
    let boxes = 0;
    let products = 0;

    flowBundles.forEach((bundle) => {
        products += bundle.numberOfUnits;
        bundle.boxes.forEach((box) => {
            boxes += 1;
            deliveryIds.add(box.deliveryId);
            box.products.forEach((product) => referenceIds.add(product._id));
        });
    });

    return {
        bundles: flowBundles.length,
        boxes,
        deliveries: deliveryIds.size,
        products,
        references: referenceIds.size,
    };
};

export const FlowSelectionButtons = ({ bundles, selectedIds, onSelectionChange }: FlowSelectionButtonsProps) => {
    const today = formatDateAsAnniversary({ dateTime: new Date(), useNewFormat: true });
    const todayBundles = bundles.filter((bundle) => bundle.scheduledPreparationDate === today);
    const flowsPresent = Object.values(ProductFlow).filter((flow) => todayBundles.some(({ type }) => type === flow));

    if (flowsPresent.length === 0) return null;

    const selectedIdSet = new Set(selectedIds);

    const toggleFlow = (flow: ProductFlow) => {
        const flowIds = todayBundles.filter(({ type }) => type === flow).map(({ _id }) => _id);
        const allSelected = flowIds.every((id) => selectedIdSet.has(id));
        onSelectionChange(
            allSelected
                ? selectedIds.filter((id) => !flowIds.includes(id))
                : [...new Set([...selectedIds, ...flowIds])],
        );
    };

    return (
        <Container>
            <Buttons>
                {flowsPresent.map((flow) => {
                    const flowBundles = todayBundles.filter(({ type }) => type === flow);
                    const isSelected = flowBundles.every(({ _id }) => selectedIdSet.has(_id));
                    const stats = computeFlowStats(flowBundles);
                    return (
                        <FlowButton key={flow} type="button" $isSelected={isSelected} onClick={() => toggleFlow(flow)}>
                            <BundleTypeTag type={flow} />
                            <Stats>
                                <Stat>
                                    <StatValue>{stats.bundles}</StatValue>
                                    <StatLabel>bundles</StatLabel>
                                </Stat>
                                <Stat>
                                    <StatValue>{stats.boxes}</StatValue>
                                    <StatLabel>caisses</StatLabel>
                                </Stat>
                                <Stat>
                                    <StatValue>{stats.deliveries}</StatValue>
                                    <StatLabel>livraisons</StatLabel>
                                </Stat>
                                <Stat>
                                    <StatValue>{stats.products}</StatValue>
                                    <StatLabel>produits</StatLabel>
                                </Stat>
                                <Stat>
                                    <StatValue>{stats.references}</StatValue>
                                    <StatLabel>réfs</StatLabel>
                                </Stat>
                            </Stats>
                        </FlowButton>
                    );
                })}
            </Buttons>
        </Container>
    );
};

const Container = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    margin: 10px 15px 0;
    padding: 10px 15px;
    background-color: ${({ theme }) => theme.cardBackgroundColor};
    border: 1px solid ${({ theme }) => theme.lightBorderColor};
    border-radius: var(--radius-lg);
`;

const Buttons = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
`;

const FlowButton = styled.button<{ $isSelected: boolean }>`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    padding: 8px 12px;
    border-radius: var(--radius-md);
    background-color: ${({ theme }) => theme.backgroundColor};
    border: 2px solid ${({ theme, $isSelected }) => ($isSelected ? theme.textColor : 'transparent')};
    transition: border-color 0.15s ease;

    &:hover {
        opacity: 0.85;
    }
`;

const Stats = styled.div`
    display: flex;
    align-items: stretch;
    gap: 10px;
`;

const Stat = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    min-width: 48px;

    &:not(:last-child) {
        border-right: 1px solid ${({ theme }) => theme.lightBorderColor};
        padding-right: 10px;
    }
`;

const StatValue = styled.span`
    font-size: 16px;
    font-weight: 700;
    color: ${({ theme }) => theme.textColor};
`;

const StatLabel = styled.span`
    font-size: 11px;
    color: ${({ theme }) => theme.infoTextColor};
`;
